import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { SPIN_APPEND, SPIN_STYLES, SpinInputModel } from "countable@model";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, distinctUntilChanged, finalize, Subject, Subscription, takeUntil } from "rxjs";
import { CoreModule } from "src/app/core/core.module";
import { ExportPlatformService } from "src/app/services/export-platform.service";
import { SharedModule } from "../../shared/shared.module";
import { UserExportHistoryComponent } from "./export-history/export-history.component";
import { UserExportAllDataComponent } from "./export-all-data/export-all-data.component";
import { CustomDataExportComponent } from "./custom-data-export/custom-data-export.component";
import { SpinDirective } from "src/app/directives/spin.directive";

@Component({
  selector: "app-user-export-data",
  templateUrl: "./export-data.component.html",
  styleUrls: ["./export-data.component.scss"],
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    CoreModule,
    SpinDirective,
    UserExportHistoryComponent,
    UserExportAllDataComponent,
    CustomDataExportComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserExportDataComponent implements OnInit, OnDestroy {
  @ViewChild("exportAllDataDailog") exportAllDataDailog: TemplateRef<any>;

  public allSubscriptions = new Subscription();
  private readonly unSubscribe$: Subject<void> = new Subject<void>();
  public clientCount$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public engagementCount$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  tabSubscriptionRef!: Subscription;
  inProgSub: Subscription;
  dialogRef: any;
  exportAllData: WritableSignal<boolean> = signal<boolean>(false);
  exportCustomData: WritableSignal<boolean> = signal<boolean>(false);
  selectedTab = 0;
  inProgressCount = 0;
  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(
    new SpinInputModel(SPIN_APPEND.HOST, SPIN_STYLES.BLUR_WITH_SPIN)
  );
  private spinCount: number = 0;

  constructor(
    private exportPlatformService: ExportPlatformService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private toaster: ToastrService
  ) {}

  ngOnInit() {
    this.inProgressCount = 0;
    this.getCounts();

    this.inProgSub = this.exportPlatformService.inProgressCount$.subscribe((response) => {
      this.inProgressCount = response ? response : 0;
    });
  }

  getCounts() {
    this.updateSpinnerConfig(true);
    this.allSubscriptions.add(
      this.exportPlatformService
        .getExportCountData()
        .pipe(
          takeUntil(this.unSubscribe$),
          distinctUntilChanged(),
          finalize(() => this.updateSpinnerConfig(false))
        )
        .subscribe({
          next: (response: any) => {
            if (response && response?.data && response?.data?.engagements) {
              this.clientCount$.next(response?.data?.clients);
              this.engagementCount$.next(response?.data?.engagements);
            }
            this.cdr.detectChanges();
          },
          error: (error: any) => {
            this.toaster.error(error.error);
          },
        })
    );

    this.tabSubscriptionRef = this.exportPlatformService.navigateToHistoryTab$.subscribe((res) => {
      if (res) {
        this.selectedTab = 1;
        this.exportAllData.set(false);
        this.exportCustomData.set(false);
      }
    });
  }

  openExportAllDataPopup() {
    this.dialogRef = this.dialog.open(this.exportAllDataDailog, {});
  }

  proceedWithExportAll() {
    this.dialogRef.close();
    this.exportAllData.set(true);
    this.cdr.detectChanges();
  }

  goBackToExportData() {
    this.exportAllData.set(false);
    this.exportCustomData.set(false);
    this.cdr.detectChanges();
  }

  onTabChange(value) {
    this.selectedTab = value;
    if (value == 1) {
      this.exportPlatformService.navigateToHistoryTab$.next(false);
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();

    if (this.tabSubscriptionRef) {
      this.tabSubscriptionRef.unsubscribe();
    }

    if (this.inProgSub) {
      this.inProgSub.unsubscribe();
    }
    this.exportPlatformService.navigateToHistoryTab$.next(false);
  }

  private updateSpinnerConfig(value: boolean) {
    value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1));
    const spinner = this.spinnerConfig();
    this.spinnerConfig.set({
      ...spinner,
      isSpinShown: this.spinCount > 0,
    });
  }
}
